import React, { useState, useEffect, useCallback } from 'react';
import { signOut, getCurrentUser } from 'aws-amplify/auth';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { toast } from 'sonner';
import { lambdaService, FileInfo } from '../../services/lambdaService';
import { ThemeToggle } from '../theme/theme-toggle';
import { APP_VERSION } from '../../version';
import {
  Files,
  Upload,
  BarChart3,
  User,
  LogOut,
  RefreshCw,
  Activity,
  Search,
  Database,
  Columns,
  CheckCircle,
  Clock,
  AlertCircle,
  Loader2,
  Play,
  Sparkles,
  Gauge,
  Link2,
  Plug,
  MoreHorizontal,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

// Simple mini bar chart component
const MiniBarChart: React.FC<{ data: { value: string; count: number }[]; maxCount: number }> = ({ data, maxCount }) => {
  return (
    <div className="flex items-end gap-0.5 h-8">
      {data.slice(0, 5).map((item, i) => {
        const height = maxCount > 0 ? (item.count / maxCount) * 100 : 0;
        return (
          <div
            key={i}
            className="flex-1 bg-primary/60 rounded-t-sm transition-all hover:bg-primary"
            style={{ height: `${Math.max(height, 5)}%` }}
            title={`${item.value}: ${item.count.toLocaleString()}`}
          />
        );
      })}
    </div>
  );
};

// Simple distribution bar for numeric columns
const DistributionBar: React.FC<{ min: number; max: number; mean: number }> = ({ min, max, mean }) => {
  const range = max - min;
  const meanPosition = range > 0 ? ((mean - min) / range) * 100 : 50;

  return (
    <div className="relative h-3 bg-muted rounded-full overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-primary/30 via-primary/60 to-primary/30" />
      <div
        className="absolute top-0 bottom-0 w-0.5 bg-primary"
        style={{ left: `${meanPosition}%` }}
        title={`Mean: ${mean.toLocaleString()}`}
      />
    </div>
  );
};

// Null percentage indicator
const NullIndicator: React.FC<{ nullCount: number; totalCount: number }> = ({ nullCount, totalCount }) => {
  const nullPercent = totalCount > 0 ? (nullCount / totalCount) * 100 : 0;

  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full ${nullPercent > 50 ? 'bg-red-500' : nullPercent > 20 ? 'bg-yellow-500' : 'bg-green-500'}`}
          style={{ width: `${100 - nullPercent}%` }}
        />
      </div>
      <span className="text-[10px] text-muted-foreground w-10 text-right">
        {nullPercent.toFixed(0)}% null
      </span>
    </div>
  );
};

// Types for column statistics
interface ColumnStats {
  name: string;
  dtype: string;
  status: 'pending' | 'loading' | 'complete' | 'error';
  error?: string;
  stats?: {
    count?: number;
    null_count?: number;
    unique_count?: number;
    min?: number | string;
    max?: number | string;
    mean?: number;
    std?: number;
    top_values?: { value: string; count: number }[];
  };
}

interface SchemaInfo {
  columns: { name: string; dtype: string }[];
  totalRows: number;
}

// Cache types
interface CachedStatsData {
  schema: SchemaInfo;
  columnStats: ColumnStats[];
  timestamp: number;
}

// Cache helper functions
const STATS_CACHE_KEY_PREFIX = 'bigsplice_stats_';
const CACHE_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const AUTO_ANALYZE_THRESHOLD = 10; // Auto-analyze if <= 10 columns
const PARALLEL_BATCH_SIZE = 5; // Process 5 columns in parallel (reduced to avoid API throttling)
const PAUSE_AFTER_COLUMNS = 25; // Pause and ask user to continue after this many columns

const getCacheKey = (fileName: string): string => {
  return `${STATS_CACHE_KEY_PREFIX}${fileName}`;
};

const getCachedStats = (fileName: string): CachedStatsData | null => {
  try {
    const cached = localStorage.getItem(getCacheKey(fileName));
    if (!cached) return null;

    const data: CachedStatsData = JSON.parse(cached);

    if (Date.now() - data.timestamp > CACHE_EXPIRY_MS) {
      localStorage.removeItem(getCacheKey(fileName));
      return null;
    }

    return data;
  } catch (e) {
    console.warn('Failed to read stats cache:', e);
    return null;
  }
};

const setCachedStats = (fileName: string, schema: SchemaInfo, columnStats: ColumnStats[]): void => {
  try {
    const data: CachedStatsData = {
      schema,
      columnStats,
      timestamp: Date.now()
    };
    localStorage.setItem(getCacheKey(fileName), JSON.stringify(data));
  } catch (e) {
    console.warn('Failed to write stats cache:', e);
  }
};

const updateCachedColumnStats = (fileName: string, columnIndex: number, stats: ColumnStats): void => {
  try {
    const cached = localStorage.getItem(getCacheKey(fileName));
    if (!cached) return;

    const data: CachedStatsData = JSON.parse(cached);
    if (data.columnStats && data.columnStats[columnIndex]) {
      data.columnStats[columnIndex] = stats;
      data.timestamp = Date.now();
      localStorage.setItem(getCacheKey(fileName), JSON.stringify(data));
    }
  } catch (e) {
    console.warn('Failed to update stats cache:', e);
  }
};

const clearStatsCache = (fileName: string): void => {
  try {
    localStorage.removeItem(getCacheKey(fileName));
  } catch (e) {
    console.warn('Failed to clear stats cache:', e);
  }
};

export const StatsDashboardPage: React.FC = () => {
  const [files, setFiles] = useState<FileInfo[]>([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(true);
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');

  // Stats loading state
  const [schemaInfo, setSchemaInfo] = useState<SchemaInfo | null>(null);
  const [columnStats, setColumnStats] = useState<ColumnStats[]>([]);
  const [isLoadingSchema, setIsLoadingSchema] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isFromCache, setIsFromCache] = useState(false);
  const [needsManualStart, setNeedsManualStart] = useState(false);
  const [currentBatch, setCurrentBatch] = useState<number>(-1); // Track which batch we're on
  const [isPaused, setIsPaused] = useState(false); // Paused waiting for user to continue

  const navigate = useNavigate();

  useEffect(() => {
    loadInitialData();
  }, []);

  // Process columns in batches of up to PARALLEL_BATCH_SIZE
  useEffect(() => {
    if (isProcessing && currentBatch >= 0 && columnStats.length > 0) {
      processBatch(currentBatch);
    }
  }, [currentBatch, isProcessing]);

  const loadInitialData = async () => {
    try {
      const user = await getCurrentUser();
      setUserEmail(user.signInDetails?.loginId || 'User');

      const response = await lambdaService.listFiles();
      if (response.success && response.files) {
        setFiles(response.files);
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast.error('Failed to load files');
    } finally {
      setIsLoadingFiles(false);
    }
  };

  const loadSchema = async (fileName: string, forceRefresh: boolean = false, autoStartAnalysis: boolean = true) => {
    setIsLoadingSchema(true);
    setColumnStats([]);
    setSchemaInfo(null);
    setCurrentBatch(-1);
    setIsProcessing(false);
    setIsFromCache(false);
    setNeedsManualStart(false);
    setIsPaused(false);

    // Check cache first (unless forcing refresh)
    if (!forceRefresh) {
      const cached = getCachedStats(fileName);
      if (cached) {
        const allComplete = cached.columnStats.every(c => c.status === 'complete');
        if (allComplete) {
          setSchemaInfo(cached.schema);
          setColumnStats(cached.columnStats);
          setIsLoadingSchema(false);
          setIsFromCache(true);
          toast.success(`Loaded ${cached.columnStats.length} columns from cache`);
          return;
        }
        // If partially complete, use cached data but continue processing
        setSchemaInfo(cached.schema);
        setColumnStats(cached.columnStats);

        const firstIncomplete = cached.columnStats.findIndex(c => c.status !== 'complete');
        if (firstIncomplete >= 0) {
          // Check if we should auto-start or wait for manual trigger
          if (cached.columnStats.length > AUTO_ANALYZE_THRESHOLD && autoStartAnalysis) {
            setNeedsManualStart(true);
            setIsLoadingSchema(false);
            toast.info(`${cached.columnStats.length} columns found. Click "Analyze" to compute stats.`);
            return;
          }
          const startBatch = Math.floor(firstIncomplete / PARALLEL_BATCH_SIZE);
          toast.info(`Resuming from batch ${startBatch + 1} (column ${firstIncomplete + 1}) of ${cached.columnStats.length}`);
          setIsLoadingSchema(false);
          setIsProcessing(true);
          setCurrentBatch(startBatch);
          return;
        }
      }
    }

    try {
      const schemaQuery = `
result = df.lazy().head(1).collect()
import json
schema_info = []
for col in result.columns:
    dtype = str(result[col].dtype)
    schema_info.append({"name": col, "dtype": dtype})
total_rows = df.lazy().select(pl.len()).collect().item()
result = pl.DataFrame({"schema": [json.dumps({"columns": schema_info, "total_rows": total_rows})]})
`;

      const analysisResult = await lambdaService.runAnalysis(fileName, schemaQuery);

      if (analysisResult && analysisResult.result && analysisResult.result.length > 0) {
        const schemaData = JSON.parse(analysisResult.result[0].schema);
        const newSchemaInfo = {
          columns: schemaData.columns,
          totalRows: schemaData.total_rows
        };
        setSchemaInfo(newSchemaInfo);

        const initialStats: ColumnStats[] = schemaData.columns.map((col: { name: string; dtype: string }) => ({
          name: col.name,
          dtype: col.dtype,
          status: 'pending' as const
        }));
        setColumnStats(initialStats);

        setCachedStats(fileName, newSchemaInfo, initialStats);

        // Check if we should auto-start or wait for manual trigger
        if (schemaData.columns.length > AUTO_ANALYZE_THRESHOLD) {
          setNeedsManualStart(true);
          toast.success(`Found ${schemaData.columns.length} columns, ${schemaData.total_rows.toLocaleString()} rows. Click "Analyze" to compute stats.`);
        } else {
          toast.success(`Found ${schemaData.columns.length} columns, ${schemaData.total_rows.toLocaleString()} rows`);
          setIsProcessing(true);
          setCurrentBatch(0);
        }
      } else {
        toast.error('Failed to load schema');
      }
    } catch (error) {
      console.error('Error loading schema:', error);
      toast.error('Failed to load file schema');
    } finally {
      setIsLoadingSchema(false);
    }
  };

  const startAnalysis = () => {
    setNeedsManualStart(false);
    setIsPaused(false);
    const firstIncomplete = columnStats.findIndex(c => c.status !== 'complete');
    if (firstIncomplete >= 0) {
      const startBatch = Math.floor(firstIncomplete / PARALLEL_BATCH_SIZE);
      setIsProcessing(true);
      setCurrentBatch(startBatch);
    }
  };

  const continueAnalysis = () => {
    setIsPaused(false);
    const firstIncomplete = columnStats.findIndex(c => c.status !== 'complete');
    if (firstIncomplete >= 0) {
      const nextBatch = Math.floor(firstIncomplete / PARALLEL_BATCH_SIZE);
      setIsProcessing(true);
      setCurrentBatch(nextBatch);
    }
  };

  // Process a single column and return the result (used by processBatch)
  const processColumnAsync = useCallback(async (index: number, col: ColumnStats): Promise<{ index: number; result: ColumnStats }> => {
    if (!selectedFileName) {
      return { index, result: { ...col, status: 'error' as const, error: 'No file selected' } };
    }

    try {
      const colName = col.name;
      const isNumeric = col.dtype.includes('Int') || col.dtype.includes('Float') || col.dtype.includes('UInt');
      const isBoolean = col.dtype.includes('Bool');

      let statsQuery: string;

      if (isNumeric) {
        statsQuery = `
result = df.lazy().select([
    pl.col("${colName}").count().alias("count"),
    pl.col("${colName}").null_count().alias("null_count"),
    pl.col("${colName}").n_unique().alias("unique_count"),
    pl.col("${colName}").min().alias("min"),
    pl.col("${colName}").max().alias("max"),
    pl.col("${colName}").mean().alias("mean"),
    pl.col("${colName}").std().alias("std")
]).collect()
`;
      } else if (isBoolean) {
        statsQuery = `
result = df.lazy().select([
    pl.col("${colName}").count().alias("count"),
    pl.col("${colName}").null_count().alias("null_count"),
    pl.col("${colName}").sum().cast(pl.Int64).alias("true_count")
]).collect()
`;
      } else {
        statsQuery = `
stats = df.lazy().select([
    pl.col("${colName}").count().alias("count"),
    pl.col("${colName}").null_count().alias("null_count"),
    pl.col("${colName}").n_unique().alias("unique_count")
]).collect()

top_values = df.lazy().group_by("${colName}").agg(pl.count().alias("cnt")).sort("cnt", descending=True).head(10).collect()

import json
top_list = []
for row in top_values.iter_rows(named=True):
    val = row["${colName}"]
    cnt = row["cnt"]
    top_list.append({"value": str(val) if val is not None else "(null)", "count": int(cnt)})

result = pl.DataFrame({
    "count": [int(stats["count"][0])],
    "null_count": [int(stats["null_count"][0])],
    "unique_count": [int(stats["unique_count"][0])],
    "top_values": [json.dumps(top_list)]
})
`;
      }

      const analysisResult = await lambdaService.runAnalysis(selectedFileName, statsQuery);

      if (analysisResult && analysisResult.result && analysisResult.result.length > 0) {
        const row = analysisResult.result[0];

        const stats: ColumnStats['stats'] = {
          count: row.count,
          null_count: row.null_count,
          unique_count: row.unique_count,
          min: row.min,
          max: row.max,
          mean: row.mean,
          std: row.std
        };

        if (row.top_values) {
          try {
            stats.top_values = JSON.parse(row.top_values);
          } catch (e) {
            console.warn('Failed to parse top_values:', e);
          }
        }

        if (isBoolean && row.true_count !== undefined) {
          stats.top_values = [
            { value: 'true', count: row.true_count },
            { value: 'false', count: (row.count || 0) - (row.true_count || 0) }
          ];
        }

        return { index, result: { ...col, status: 'complete' as const, stats } };
      } else {
        throw new Error('No result returned');
      }
    } catch (error) {
      console.error(`Error processing column ${col.name}:`, error);
      return { index, result: { ...col, status: 'error' as const, error: String(error) } };
    }
  }, [selectedFileName]);

  // Process a batch of columns in parallel
  const processBatch = useCallback(async (batchIndex: number) => {
    if (!selectedFileName || columnStats.length === 0) {
      setIsProcessing(false);
      return;
    }

    const startIdx = batchIndex * PARALLEL_BATCH_SIZE;
    const endIdx = Math.min(startIdx + PARALLEL_BATCH_SIZE, columnStats.length);

    // Get columns to process in this batch (only pending/error ones)
    const batchColumns: { index: number; col: ColumnStats }[] = [];
    for (let i = startIdx; i < endIdx; i++) {
      if (columnStats[i] && columnStats[i].status !== 'complete') {
        batchColumns.push({ index: i, col: columnStats[i] });
      }
    }

    if (batchColumns.length === 0) {
      // This batch has no work, move to next batch or finish
      if (endIdx < columnStats.length) {
        setCurrentBatch(batchIndex + 1);
      } else {
        setIsProcessing(false);
        toast.success('All columns processed!');
      }
      return;
    }

    // Mark all columns in batch as loading
    setColumnStats(prev => prev.map((c, i) => {
      if (i >= startIdx && i < endIdx && c.status !== 'complete') {
        return { ...c, status: 'loading' as const };
      }
      return c;
    }));

    // Process all columns in parallel
    const promises = batchColumns.map(({ index, col }) => processColumnAsync(index, col));
    const results = await Promise.all(promises);

    // Update all results at once
    setColumnStats(prev => {
      const updated = [...prev];
      for (const { index, result } of results) {
        updated[index] = result;
        // Update cache for each result
        if (selectedFileName) {
          updateCachedColumnStats(selectedFileName, index, result);
        }
      }
      return updated;
    });

    // Check if we should pause after processing this batch
    const processedSoFar = columnStats.filter(c => c.status === 'complete').length + results.length;
    const remainingColumns = columnStats.length - processedSoFar;

    if (endIdx < columnStats.length) {
      // Check if we've hit a pause point (every PAUSE_AFTER_COLUMNS columns)
      const nextPausePoint = Math.ceil(processedSoFar / PAUSE_AFTER_COLUMNS) * PAUSE_AFTER_COLUMNS;
      if (processedSoFar >= nextPausePoint && remainingColumns > 0) {
        // Pause and wait for user to continue
        setIsProcessing(false);
        setIsPaused(true);
        toast.info(`Processed ${processedSoFar} columns. Click to analyze the next ${Math.min(PAUSE_AFTER_COLUMNS, remainingColumns)} columns.`);
      } else {
        setCurrentBatch(batchIndex + 1);
      }
    } else {
      setIsProcessing(false);
      setIsPaused(false);
      toast.success('All columns processed!');
    }
  }, [selectedFileName, columnStats, processColumnAsync]);

  const handleFileSelect = (fileName: string) => {
    setSelectedFileName(fileName);
    loadSchema(fileName);
  };

  const handleRefresh = () => {
    if (selectedFileName) {
      clearStatsCache(selectedFileName);
      loadSchema(selectedFileName, true);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  const filteredFiles = files.filter((file) =>
    file.fileName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const completedColumns = columnStats.filter(c => c.status === 'complete').length;
  const totalColumns = columnStats.length;
  const progressPercent = totalColumns > 0 ? (completedColumns / totalColumns) * 100 : 0;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-14 items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/files" className="flex items-center gap-2">
              <Activity className="h-6 w-6 text-primary" />
              <span className="font-bold text-lg">BigSplice</span>
            </Link>
            <nav className="hidden md:flex items-center gap-4">
              <Link
                to="/dashboard"
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
              >
                <Files className="h-4 w-4" />
                Files
              </Link>
              <Link
                to="/upload"
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
              >
                <Upload className="h-4 w-4" />
                Upload
              </Link>
              <Link
                to="/analyze"
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
              >
                <BarChart3 className="h-4 w-4" />
                Analyze
              </Link>
              <Link
                to="/introspect"
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
              >
                <Sparkles className="h-4 w-4" />
                Introspect
              </Link>
              <Link
                to="/stats-dashboard"
                className="flex items-center gap-1 text-sm font-medium text-foreground"
              >
                <Gauge className="h-4 w-4" />
                Stats
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
                    <MoreHorizontal className="h-4 w-4" />
                    More
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem asChild>
                    <Link to="/ai-connections" className="flex items-center gap-2">
                      <Link2 className="h-4 w-4" />
                      AI Connections
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/integrations" className="flex items-center gap-2">
                      <Plug className="h-4 w-4" />
                      Integrations
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/usage" className="flex items-center gap-2">
                      <Database className="h-4 w-4" />
                      Usage
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Profile
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </nav>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs text-muted-foreground">v{APP_VERSION}</span>
            <ThemeToggle />
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <User className="h-4 w-4" />
              {userEmail}
            </div>
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="container py-6 space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Stats Dashboard</h1>
            <p className="text-muted-foreground">
              Column statistics computed via the analysis Lambda
            </p>
          </div>
          <div className="flex gap-2">
            {needsManualStart && (
              <Button
                onClick={startAnalysis}
                disabled={isProcessing}
              >
                <Play className="h-4 w-4 mr-2" />
                Analyze All Columns
              </Button>
            )}
            {isPaused && (
              <Button
                onClick={continueAnalysis}
                disabled={isProcessing}
              >
                <Play className="h-4 w-4 mr-2" />
                Analyze Next {Math.min(PAUSE_AFTER_COLUMNS, totalColumns - completedColumns)} Columns
              </Button>
            )}
            {selectedFileName && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={isLoadingSchema || isProcessing}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${(isLoadingSchema || isProcessing) ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            )}
          </div>
        </div>

        {/* File Selector */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Select File</CardTitle>
            <CardDescription>
              Choose a file to compute column statistics
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative mb-2">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search files..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Select
                  value={selectedFileName || ''}
                  onValueChange={handleFileSelect}
                  disabled={isLoadingFiles}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={isLoadingFiles ? 'Loading files...' : 'Select a file'} />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredFiles.map((file) => (
                      <SelectItem key={file.s3Key || file.fileName} value={file.fileName}>
                        <div className="flex items-center gap-2">
                          <span className="truncate max-w-[300px]">{file.fileName}</span>
                          <Badge variant="secondary" className="text-xs">
                            {formatFileSize(file.fileSize)}
                          </Badge>
                        </div>
                      </SelectItem>
                    ))}
                    {filteredFiles.length === 0 && !isLoadingFiles && (
                      <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                        No files found
                      </div>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Summary stats */}
              {schemaInfo && (
                <div className="flex gap-4 items-center">
                  <div className="flex items-center gap-2 text-sm">
                    <Database className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Rows:</span>
                    <span className="font-medium">{schemaInfo.totalRows.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Columns className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Columns:</span>
                    <span className="font-medium">{schemaInfo.columns.length}</span>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Progress */}
        {columnStats.length > 0 && (
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">
                    {isFromCache ? 'Loaded from cache:' : needsManualStart ? 'Ready to analyze:' : 'Processing columns:'} {completedColumns} / {totalColumns}
                  </span>
                  {isFromCache && (
                    <Badge variant="secondary" className="text-xs">
                      Cached
                    </Badge>
                  )}
                  {needsManualStart && (
                    <Badge variant="outline" className="text-xs">
                      Click Analyze to start
                    </Badge>
                  )}
                </div>
                <span className="text-sm text-muted-foreground">
                  {progressPercent.toFixed(0)}%
                </span>
              </div>
              <Progress value={progressPercent} className="h-2" />
            </CardContent>
          </Card>
        )}

        {/* Column Stats Grid */}
        {columnStats.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {columnStats.map((col) => {
              const isNumeric = col.dtype.includes('Int') || col.dtype.includes('Float') || col.dtype.includes('UInt');
              const maxTopValue = col.stats?.top_values?.reduce((max, tv) => Math.max(max, tv.count), 0) || 0;

              return (
                <Card key={col.name} className={`relative overflow-hidden ${col.status === 'loading' ? 'border-primary' : ''}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between gap-2">
                      <CardTitle className="text-sm font-medium truncate flex-1" title={col.name}>
                        {col.name}
                      </CardTitle>
                      <div className="flex-shrink-0">
                        {col.status === 'pending' && <Clock className="h-4 w-4 text-muted-foreground" />}
                        {col.status === 'loading' && <Loader2 className="h-4 w-4 text-primary animate-spin" />}
                        {col.status === 'complete' && <CheckCircle className="h-4 w-4 text-green-500" />}
                        {col.status === 'error' && <AlertCircle className="h-4 w-4 text-red-500" />}
                      </div>
                    </div>
                    <Badge variant="outline" className="text-xs w-fit">
                      {col.dtype}
                    </Badge>
                  </CardHeader>
                  <CardContent className="text-xs space-y-2">
                    {col.status === 'pending' && (
                      <p className="text-muted-foreground">Waiting...</p>
                    )}
                    {col.status === 'loading' && (
                      <p className="text-muted-foreground">Computing stats...</p>
                    )}
                    {col.status === 'error' && (
                      <p className="text-red-500 break-words line-clamp-2" title={col.error}>Error: {col.error}</p>
                    )}
                    {col.status === 'complete' && col.stats && (
                      <div className="space-y-2">
                        {/* Null indicator */}
                        {col.stats.count !== undefined && col.stats.null_count !== undefined && (
                          <NullIndicator nullCount={col.stats.null_count} totalCount={col.stats.count + col.stats.null_count} />
                        )}

                        {/* Numeric distribution */}
                        {isNumeric && col.stats.min !== undefined && col.stats.max !== undefined && col.stats.mean !== undefined &&
                         typeof col.stats.min === 'number' && typeof col.stats.max === 'number' && (
                          <div className="space-y-1">
                            <DistributionBar min={col.stats.min} max={col.stats.max} mean={col.stats.mean} />
                            <div className="flex justify-between text-[10px] text-muted-foreground">
                              <span>{col.stats.min.toLocaleString()}</span>
                              <span className="font-medium">μ {col.stats.mean.toFixed(1)}</span>
                              <span>{col.stats.max.toLocaleString()}</span>
                            </div>
                          </div>
                        )}

                        {/* Bar chart for categorical */}
                        {col.stats.top_values && col.stats.top_values.length > 0 && (
                          <div className="space-y-1">
                            <MiniBarChart data={col.stats.top_values} maxCount={maxTopValue} />
                            <div className="text-[10px] text-muted-foreground">
                              {col.stats.unique_count?.toLocaleString()} unique values
                            </div>
                          </div>
                        )}

                        {/* Stats grid */}
                        <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 pt-1 border-t">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Count:</span>
                            <span className="truncate ml-1">{col.stats.count?.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Nulls:</span>
                            <span className="truncate ml-1">{col.stats.null_count?.toLocaleString()}</span>
                          </div>
                          {col.stats.unique_count !== undefined && !col.stats.top_values && (
                            <div className="flex justify-between col-span-2">
                              <span className="text-muted-foreground">Unique:</span>
                              <span className="truncate ml-1">{col.stats.unique_count?.toLocaleString()}</span>
                            </div>
                          )}
                        </div>

                        {/* Top values list */}
                        {col.stats.top_values && col.stats.top_values.length > 0 && (
                          <div className="space-y-0.5 max-h-20 overflow-y-auto">
                            {col.stats.top_values.slice(0, 5).map((tv, i) => (
                              <div key={i} className="flex justify-between gap-1">
                                <span className="truncate flex-1 text-muted-foreground" title={tv.value}>
                                  {tv.value}
                                </span>
                                <span className="flex-shrink-0 tabular-nums">{tv.count.toLocaleString()}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Empty state */}
        {!selectedFileName && !isLoadingFiles && (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Activity className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No file selected</h3>
              <p className="text-muted-foreground text-center max-w-md">
                Select a file from the dropdown above to compute column statistics.
                For files with more than 10 columns, you'll need to click "Analyze" to start.
              </p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default StatsDashboardPage;
